import { jsonGet, jsonPost, jsonPut, jsonDelete, downloadFile, prefix, getAuthHeaders } from "../../Boilerplate/api";
import { contentDispositionAttachment } from "./_Shared/contentDisposition";
export { contentDispositionAttachment };

// Candidates
export const getCandidates = (page = 1, pageSize = 50, role = null, projectId = null, sort = null, consentStatus = null, consentOverdue = false, openToWork = false) =>
  jsonGet(`candidates?page=${page}&pageSize=${pageSize}` +
    `${role ? `&role=${role}` : ""}` +
    `${projectId ? `&projectId=${projectId}` : ""}` +
    `${sort ? `&sort=${sort}` : ""}` +
    `${consentStatus ? `&consentStatus=${consentStatus}` : ""}` +
    `${consentOverdue ? `&consentOverdue=true` : ""}` +
    `${openToWork ? `&openToWork=true` : ""}`);
export const getCandidate = (id) => jsonGet(`candidates/${id}`);
export const getCandidateFieldSources = (id) => jsonGet(`candidates/${id}/field-sources`);
export const getFieldHistory = (id, field, entryId) =>
  jsonGet(`candidates/${id}/history?field=${encodeURIComponent(field)}${entryId ? `&entryId=${encodeURIComponent(entryId)}` : ""}`);
export const restoreEntry = (id, activityId) => jsonPost(`candidates/${id}/restore-entry`, { activityId });
export const getActivityFeed = (limit = 50, before) =>
  jsonGet(`activity-feed?limit=${limit}${before ? `&before=${encodeURIComponent(before)}` : ""}`);
export const getFeedUnreadCount = () => jsonGet(`activity-feed/unread-count`);
export const markFeedSeen = () => jsonPost(`activity-feed/mark-seen`, {});
export const searchCandidates = (q) =>
  jsonGet(`candidates/search?q=${encodeURIComponent(q)}`);
// Dedicated fetch (not jsonPost) so in-flight searches can be ABORTED when a newer one
// supersedes them — the backend threads the abort into its Mongo/embedding work, so dead
// requests stop consuming the cluster instead of piling up behind the debounce.
export async function searchCandidatesAdvanced(request, { signal } = {}) {
  const response = await fetch(`${prefix}/candidates/search`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify(request),
    signal,
  });
  if (!response.ok) throw new Error(`Search failed (${response.status})`);
  return response.json();
}
export const getCandidateFilterOptions = () =>
  jsonGet(`candidates/filter-options`);
// Slim map points for the candidate map view; params = { role, projectId, consentStatus, consentOverdue, openToWork }.
export const getCandidatesMap = (params = {}) => {
  const qs = Object.entries(params)
    .filter(([, v]) => v !== null && v !== undefined && v !== false && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&");
  return jsonGet(`candidates/map${qs ? `?${qs}` : ""}`);
};
export const getCandidatesByCompany = (companyId) =>
  jsonGet(`candidates/by-company/${companyId}`);
// GDPR consent — staff (candidate-scoped)
export const getCandidateConsent = (id) => jsonGet(`candidates/${id}/consent`);
export const createConsentRequest = (id, dto) =>
  jsonPost(`candidates/${id}/consent/requests`, dto);
export const resendConsentRequest = (id, reqId) =>
  jsonPost(`candidates/${id}/consent/requests/${reqId}/resend`);
export const revokeConsentRequest = (id, reqId) =>
  jsonPost(`candidates/${id}/consent/requests/${reqId}/revoke`);
export const overrideConsent = (id, dto) => jsonPut(`candidates/${id}/consent`, dto);
export const downloadConsentCertificate = (id, reqId) =>
  downloadFile(`candidates/${id}/consent/requests/${reqId}/certificate`);
export const bulkConsentRequest = (dto) => jsonPost(`consent/requests/bulk`, dto);

// GDPR consent — tenant settings + chain verification
export const getConsentSettings = () => jsonGet(`settings/consent`);
export const updateConsentSettings = (dto) => jsonPut(`settings/consent`, dto);
export const verifyConsentChain = () => jsonGet(`consent/verify`);
// Self-test: email myself a real consent email linking to an isolated preview microsite.
export const sendConsentTest = () => jsonPost(`settings/consent/test-send`, {});

// GDPR data export — tenant-wide ZIP (admin-only). Kick off a job, poll it, download the result.
export const createDataExport = () => jsonPost(`exports`, {});
export const getDataExportStatus = (jobId) => jsonGet(`exports/jobs/${jobId}`);
export const listDataExports = () => jsonGet(`exports`);
export const downloadDataExport = (id) => downloadFile(`exports/${id}/download`);

// GDPR consent — public microsite (unauthenticated; token from email)
export const getPublicConsent = (token) => jsonGet(`public/consent/${token}`);
// Isolated self-test preview — same shape, decrypted from a stateless token; records nothing.
export const getConsentPreview = (token) => jsonGet(`public/consent/preview/${token}`);
export const respondPublicConsent = (token, approve) =>
  jsonPost(`public/consent/${token}/respond`, { approve });
export const withdrawPublicConsent = (token) =>
  jsonPost(`public/consent/${token}/withdraw`);

export const createCandidate = (dto) => jsonPost(`candidates`, dto);
export const bulkFromLinkedIn = (projectId, linkedInUrls) =>
  jsonPost(`candidates/bulk-from-linkedin`, { projectId, linkedInUrls });

// Multipart intake: ONE candidate from dropped files (each becomes an attachment) plus an optional
// LinkedIn URL, optional typed scalar fields, and an optional project. Can't use jsonPost (it forces
// a JSON content-type) — hand-roll the fetch so the browser sets the multipart boundary, and re-add
// the 402 upgrade-gate handling the shared client would otherwise provide.
export async function intakeCandidate({ files = [], linkedInUrl, fields = {}, projectId } = {}) {
  const form = new FormData();
  for (const file of files) form.append("files", file, file.name);
  if (linkedInUrl) form.append("linkedInUrl", linkedInUrl);
  if (projectId) form.append("projectId", projectId);
  for (const [key, value] of Object.entries(fields)) {
    if (value != null && `${value}`.trim() !== "") form.append(key, value);
  }

  const response = await fetch(`${prefix}/candidates`, {
    method: "POST",
    headers: getAuthHeaders(), // no content-type → browser sets the multipart boundary
    body: form,
  });

  if (response.status === 402) {
    let payload = null;
    try { payload = await response.clone().json(); } catch { /* non-JSON 402 */ }
    if (typeof window !== "undefined")
      window.dispatchEvent(new CustomEvent("upgrade-required", { detail: payload }));
    const err = new Error(payload?.message || "Upgrade required for this action.");
    err.status = 402;
    err.upgradeUrl = payload?.upgradeUrl || "/plans";
    throw err;
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || body.title || `Request failed (${response.status})`);
  }

  return response.json();
}
export const updateCandidate = (id, dto) => jsonPut(`candidates/${id}`, dto);
export const setCandidateWatchlist = (id, isWatchlist) =>
  jsonPut(`candidates/${id}/watchlist`, { isWatchlist });
export const setCandidateSourcedBy = (id, sourcedByCandidateIds) =>
  jsonPut(`candidates/${id}/sourced-by`, { sourcedByCandidateIds });
export const deleteCandidate = (id) => jsonDelete(`candidates/${id}`);
export const getDeletedCandidates = () => jsonGet(`candidates/deleted`);
export const restoreCandidate = (id) => jsonPost(`candidates/${id}/restore`);
export const getCandidateSources = (candidateId) =>
  jsonGet(`candidates/${candidateId}/sources`);
export const deleteCandidateSource = (candidateId, sourceId) =>
  jsonDelete(`candidates/${candidateId}/sources/${sourceId}`);

// projectId files the upload for one search project (transcript analysis attributes it there).
export async function uploadCandidateSource(candidateId, file, sourceType = "CvUpload", projectId = null) {
  const name = file.name.toLowerCase();
  const contentType = name.endsWith(".pdf") ? "application/pdf"
    : name.endsWith(".docx") ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    : name.endsWith(".doc") ? "application/msword"
    : name.endsWith(".rtf") ? "application/rtf"
    : name.endsWith(".vtt") ? "text/vtt"
    : name.endsWith(".txt") ? "text/plain"
    : file.type || "application/octet-stream";
  const query = new URLSearchParams({ sourceType });
  if (projectId) query.set("projectId", projectId);
  const response = await fetch(`${prefix}/candidates/${candidateId}/sources?${query}`, {
    method: "POST",
    headers: {
      "Content-Type": contentType,
      "Content-Disposition": contentDispositionAttachment(file.name),
      ...getAuthHeaders(),
    },
    body: file,
  });
  if (!response.ok) throw new Error(`Upload failed (${response.status})`);
  return response.json();
}
export const enrichCandidate = (id, opts = {}) => jsonPost(`candidates/${id}/enrich`, opts);
export const refetchLinkedIn = (id, opts = {}) => jsonPost(`candidates/${id}/refetch-linkedin`, opts);
export const generateSummary = (id) => jsonPost(`candidates/${id}/generate-summary`);
// Resolve a held LinkedIn identity mismatch: accept=true re-fetches with the name gate bypassed
// (right person despite the name), accept=false removes the URL (wrong profile).
export const confirmLinkedInIdentity = (id, accept) =>
  jsonPost(`candidates/${id}/confirm-linkedin-identity`, { accept });
// Manual avatar. Multipart, so hand-roll the fetch (jsonPost forces a JSON content-type).
export async function uploadCandidatePhoto(id, file) {
  const form = new FormData();
  form.append("file", file, file.name);
  const response = await fetch(`${prefix}/candidates/${id}/photo`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: form,
  });
  if (!response.ok) throw new Error(`Photo upload failed (${response.status})`);
  return response.json();
}
export const deleteCandidatePhoto = (id) => jsonDelete(`candidates/${id}/photo`);
export const getCandidateProjects = (id) => jsonGet(`candidates/${id}/projects`);
export const getCandidateClientLinks = (id) => jsonGet(`candidates/${id}/client-links`);
export const removeWebFindings = (candidateId, sourceId, removeUrls) =>
  jsonPut(`candidates/${candidateId}/sources/${sourceId}/remove-findings`, { removeUrls });
export const submitTranscript = (candidateId, dto) =>
  jsonPost(`candidates/${candidateId}/transcripts`, dto);
export const getTranscriptAnalysis = (candidateId, sourceId) =>
  jsonGet(`candidates/${candidateId}/sources/${sourceId}/analysis`);
export const confirmProjectLink = (candidateId, sourceId, projectId) =>
  jsonPost(`candidates/${candidateId}/sources/${sourceId}/confirm-project-link`, { projectId });

// Activities
export const getActivities = (candidateId, limit = 50) =>
  jsonGet(`candidates/${candidateId}/activities?limit=${limit}`);
export const createActivity = (candidateId, dto) =>
  jsonPost(`candidates/${candidateId}/activities`, dto);
export const updateActivity = (candidateId, activityId, dto) =>
  jsonPut(`candidates/${candidateId}/activities/${activityId}`, dto);
export const deleteActivity = (candidateId, activityId) =>
  jsonDelete(`candidates/${candidateId}/activities/${activityId}`);

// Projects
export const getProjects = () => jsonGet(`projects`);
export const getProject = (id) => jsonGet(`projects/${id}`);
export const createProject = (dto) => jsonPost(`projects`, dto);
export const updateProject = (id, dto) => jsonPut(`projects/${id}`, dto);
export const getProjectStatus = (projectId) =>
  jsonGet(`projects/${projectId}/status`);
export const clusterProjectStatusReasons = (projectId) =>
  jsonPost(`projects/${projectId}/status/reason-groups`, {});
export const updateProjectStatusReasonGroups = (projectId, dto) =>
  jsonPut(`projects/${projectId}/status/reason-groups`, dto);
export const getProjectCandidates = (projectId) =>
  jsonGet(`projects/${projectId}/candidates`);
export const getProjectCandidate = (projectId, pcId) =>
  jsonGet(`projects/${projectId}/candidates/${pcId}`);
export const addProjectCandidate = (projectId, dto) =>
  jsonPost(`projects/${projectId}/candidates`, dto);
// Add many candidates at once (bulk "add to project" from search). Idempotent server-side:
// candidates already on the project are skipped. Returns { requested, added, alreadyOnProject }.
export const batchAddProjectCandidates = (projectId, candidateIds, source = undefined) =>
  jsonPost(`projects/${projectId}/candidates/batch`, { candidateIds, source });
export const updateProjectCandidate = (projectId, pcId, dto) =>
  jsonPut(`projects/${projectId}/candidates/${pcId}`, dto);
export const deleteProjectCandidate = (projectId, pcId) =>
  jsonDelete(`projects/${projectId}/candidates/${pcId}`);
// Output language is resolved server-side (project override ?? tenant default), not sent by the client.
export const generateRhpDraft = (projectId, pcId, overwrite, scope = "assessmentCopy") =>
  jsonPost(`projects/${projectId}/candidates/${pcId}/draft`, { overwrite, scope });
export const approveSection = (projectId, pcId, field, sectionId = null) =>
  jsonPost(`projects/${projectId}/candidates/${pcId}/approve`, { field, sectionId });

// Clients
export const getClients = () => jsonGet(`clients`);
export const getClient = (id) => jsonGet(`clients/${id}`);
export const createClient = (dto) => jsonPost(`clients`, dto);
export const updateClient = (id, dto) => jsonPut(`clients/${id}`, dto);
export const getClientProjects = (clientId) => jsonGet(`projects?clientId=${clientId}`);

// Companies
export const getCompanies = (page = 1, pageSize = 50) =>
  jsonGet(`companies?page=${page}&pageSize=${pageSize}`);
export const searchCompanies = (q) =>
  jsonGet(`companies/search?q=${encodeURIComponent(q)}`);

// Geocoding (server-proxied Mapbox). `session` groups suggest+retrieve for Mapbox billing.
export const geoSuggest = (q, session) =>
  jsonGet(`geo/suggest?q=${encodeURIComponent(q)}&session=${encodeURIComponent(session)}`);
export const geoRetrieve = (id, session) =>
  jsonGet(`geo/retrieve?id=${encodeURIComponent(id)}&session=${encodeURIComponent(session)}`);
export const getGeoMapConfig = () => jsonGet(`geo/map-config`);
export const getCompany = (id) => jsonGet(`companies/${id}`);
export const createCompany = (dto) => jsonPost(`companies`, dto);
export const updateCompany = (id, dto) => jsonPut(`companies/${id}`, dto);
export const getCompanyDedupPreview = () => jsonGet(`companies/dedup`);
export const startCompanyDedup = (survivorIds) =>
  jsonPost(`companies/dedup`, survivorIds?.length ? { survivorIds } : {});

// Templates
export const getTemplates = () => jsonGet(`templates`);
export const getRendererInfo = () => jsonGet(`templates/renderers`);

// Document Generation
export const generateDocument = (dto) => jsonPost(`documents/generate`, dto);
export const getDocumentJobStatus = (jobId) => jsonGet(`documents/jobs/${jobId}`);
export const getGeneratedDocuments = (projectId) =>
  jsonGet(`documents/by-project/${projectId}`);
export const deleteGeneratedDocument = (docId) => jsonDelete(`documents/${docId}`);

// Project files
export const getProjectFiles = (projectId) =>
  jsonGet(`projects/${projectId}/files`);
export const deleteProjectFile = (projectId, fileId) =>
  jsonDelete(`projects/${projectId}/files/${fileId}`);

// Imports
export const getImportSessions = () => jsonGet(`imports`);
export const getImportSession = (id) => jsonGet(`imports/${id}`);
export const getImportItems = (sessionId, page = 1, pageSize = 50) =>
  jsonGet(`imports/${sessionId}/items?page=${page}&pageSize=${pageSize}`);
export const updateImportItem = (sessionId, itemId, dto) =>
  jsonPut(`imports/${sessionId}/items/${itemId}`, dto);
export const mergeImportItems = (sessionId, itemId, targetItemId) =>
  jsonPost(`imports/${sessionId}/items/${itemId}/merge/${targetItemId}`);
export const detectDuplicates = (sessionId) =>
  jsonGet(`imports/${sessionId}/duplicates`);
export const commitImport = (sessionId) =>
  jsonPost(`imports/${sessionId}/commit`);
export const deleteImportSession = (id) => jsonDelete(`imports/${id}`);
export const startGraphImport = (limit = 50) =>
  jsonPost(`imports/graph/auth?limit=${limit}`);
export const getGraphAdminConsentUrl = () =>
  jsonGet(`imports/graph/adminconsent-url`);

// Enrichment / tenant settings
export const getEnrichmentSettings = () => jsonGet(`settings/enrichment`);
export const updateEnrichmentSettings = (dto) => jsonPut(`settings/enrichment`, dto);
export const getEnrichmentCostBasis = () => jsonGet(`settings/enrichment/cost-basis`);

// Hintergrundprüfung — identity gate, then paid deep search. Poll GET {id} while a phase runs.
export const listBackgroundChecks = (candidateId) =>
  jsonGet(`background-checks?candidateId=${encodeURIComponent(candidateId)}`);
export const getBackgroundCheck = (id) => jsonGet(`background-checks/${id}`);
export const createBackgroundCheck = (candidateId, language) =>
  jsonPost(`background-checks`, { candidateId, language });
export const confirmBackgroundCheckIdentity = (id) =>
  jsonPost(`background-checks/${id}/confirm-identity`);
export const rejectBackgroundCheckIdentity = (id) =>
  jsonPost(`background-checks/${id}/reject-identity`);

// API keys
export const getApiKeys = () => jsonGet(`apikeys`);
export const createApiKey = (dto) => jsonPost(`apikeys`, dto);
export const deleteApiKey = (id) => jsonDelete(`apikeys/${id}`);

// Tags
export const getTagDefinitions = () => jsonGet(`tags`);
export const createTagDefinition = (dto) => jsonPost(`tags`, dto);
export const deleteTagDefinition = (id) => jsonDelete(`tags/${id}`);
export const bulkUpdateCandidateTags = (body) => jsonPost(`candidates/bulk-tags`, body);
export const bulkDeleteCandidates = (candidateIds) => jsonPost(`candidates/bulk-delete`, { candidateIds });

// Tasks
export const getTasks = (params = {}) => {
  const q = new URLSearchParams();
  if (params.scope) q.set("scope", params.scope);
  if (params.candidateId) q.set("candidateId", params.candidateId);
  if (params.projectId) q.set("projectId", params.projectId);
  if (params.clientId) q.set("clientId", params.clientId);
  if (params.assigneeUserId) q.set("assigneeUserId", params.assigneeUserId);
  // Paging is used by the Completed list (which can grow large); other scopes ignore it.
  if (params.page != null) q.set("page", params.page);
  if (params.pageSize != null) q.set("pageSize", params.pageSize);
  const s = q.toString();
  return jsonGet(`tasks${s ? `?${s}` : ""}`);
};
export const getTaskCounts = () => jsonGet(`tasks/counts`);
export const getTaskAssignees = () => jsonGet(`tasks/assignees`);
export const createTask = (dto) => jsonPost(`tasks`, dto);
export const updateTask = (id, dto) => jsonPut(`tasks/${id}`, dto);
export const deleteTask = (id) => jsonDelete(`tasks/${id}`);

// Notifications (in-app bell)
export const getNotifications = (limit = 30) => jsonGet(`notifications?limit=${limit}`);
export const getNotificationUnreadCount = () => jsonGet(`notifications/unread-count`);
export const markNotificationRead = (id) => jsonPost(`notifications/${id}/read`, {});
export const markAllNotificationsRead = () => jsonPost(`notifications/mark-all-read`, {});

// Chat assistant
export const sendChatMessage = (message, conversationId, pageContext) =>
  jsonPost(`chat`, { message, conversationId, pageContext });
export const getChatConversations = () => jsonGet(`chat/conversations`);
export const getChatConversation = (id) => jsonGet(`chat/${id}`);
export const deleteChatConversation = (id) => jsonDelete(`chat/${id}`);
export const getChatSuggestions = () => jsonGet(`chat/suggestions`);
